import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiFetch } from '../../lib/api';
import { notify } from '../../lib/notify';
import {
  CATALOG_IMPORT_JOB_STORAGE_KEY,
  type CatalogImportJobDto,
} from './catalogImportJobTypes';

interface CatalogImportJobContextValue {
  job: CatalogImportJobDto | null;
  isActive: boolean;
  trackJob: (job: CatalogImportJobDto) => void;
  clearJob: () => void;
  refreshJob: () => Promise<void>;
}

const CatalogImportJobContext = createContext<CatalogImportJobContextValue | null>(null);

function isActiveStatus(status: CatalogImportJobDto['status']): boolean {
  return status === 'queued' || status === 'running';
}

export function CatalogImportJobProvider({ children }: { children: ReactNode }) {
  const [job, setJob] = useState<CatalogImportJobDto | null>(null);
  const notifiedKeyRef = useRef<string | null>(null);
  const sawActiveRef = useRef(false);

  const trackJob = useCallback((next: CatalogImportJobDto) => {
    setJob(next);
    try {
      sessionStorage.setItem(CATALOG_IMPORT_JOB_STORAGE_KEY, next.jobId);
    } catch {
      // ignore storage failures
    }
  }, []);

  const clearJob = useCallback(() => {
    setJob(null);
    try {
      sessionStorage.removeItem(CATALOG_IMPORT_JOB_STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  const refreshJob = useCallback(async () => {
    let jobId: string | null = null;
    try {
      jobId = sessionStorage.getItem(CATALOG_IMPORT_JOB_STORAGE_KEY);
    } catch {
      jobId = null;
    }

    const path = jobId
      ? `/api/v1/settings/integrations/brightblue/import-export/jobs/${jobId}`
      : '/api/v1/settings/integrations/brightblue/import-export/jobs/current';

    try {
      const res = await apiFetch(path);
      if (res.status === 404) {
        if (!jobId) return;
        // Fall back to current org job if stored id expired after restart
        const current = await apiFetch(
          '/api/v1/settings/integrations/brightblue/import-export/jobs/current'
        );
        if (!current.ok) {
          clearJob();
          return;
        }
        const data = (await current.json()) as CatalogImportJobDto;
        trackJob(data);
        return;
      }
      if (!res.ok) return;
      const data = (await res.json()) as CatalogImportJobDto;
      trackJob(data);
    } catch {
      // silent — banner simply won't update this tick
    }
  }, [clearJob, trackJob]);

  useEffect(() => {
    void refreshJob();
  }, [refreshJob]);

  useEffect(() => {
    if (!job || !isActiveStatus(job.status)) return;
    const id = window.setInterval(() => {
      void refreshJob();
    }, 1000);
    return () => window.clearInterval(id);
  }, [job, refreshJob]);

  useEffect(() => {
    if (job && isActiveStatus(job.status)) {
      sawActiveRef.current = true;
    }
  }, [job]);

  useEffect(() => {
    if (!job || !sawActiveRef.current) return;
    if (job.status !== 'completed' && job.status !== 'failed') return;
    const key = `${job.jobId}:${job.status}`;
    if (notifiedKeyRef.current === key) return;
    notifiedKeyRef.current = key;
    if (job.status === 'completed') {
      notify.success(job.dryRun ? 'Catalog dry run complete' : 'Catalog import completed');
    } else if (job.error) {
      notify.error(job.error);
    }
  }, [job]);

  const value = useMemo<CatalogImportJobContextValue>(
    () => ({
      job,
      isActive: !!job && isActiveStatus(job.status),
      trackJob,
      clearJob,
      refreshJob,
    }),
    [job, trackJob, clearJob, refreshJob]
  );

  return (
    <CatalogImportJobContext.Provider value={value}>{children}</CatalogImportJobContext.Provider>
  );
}

export function useCatalogImportJob(): CatalogImportJobContextValue {
  const ctx = useContext(CatalogImportJobContext);
  if (!ctx) {
    throw new Error('useCatalogImportJob must be used within CatalogImportJobProvider');
  }
  return ctx;
}
